import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { getLocalCategoriesList } from 'entities/Category/model/selectors/categorySelectors';
import {
    fetchCustomListDetail,
    fetchCustomLists,
    getCustomListCatalogItem,
    getCustomListDetail,
    getCustomListDetailError,
    getCustomListDetailLoading,
    getCustomLists,
    getCustomListsError,
    getCustomListsLoading,
} from 'entities/CustomList';
import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { useListOrderCoverage } from 'features/AddCustomListToOrder/lib/useListOrderCoverage';
import { getRouteOrdersAddItemsList } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { type FilterOption } from 'shared/ui/FilterDropDown';

import { useAddListToOrderConfirm } from './useAddListToOrderConfirm';

import {
    type ListsToolbarState,
    useRegisterListsToolbar,
} from '../OrderAddItemsListsToolbar/OrderAddItemsListsToolbar';

export const useOrderAddItemsLists = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { listId } = useParams<{ listId?: string }>();

    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const propertyId =
        (location.state?.selectedPropertyId as string | undefined) ||
        selectedPropertyId;
    const lists = useSelector(getCustomLists);
    const isListsLoading = useSelector(getCustomListsLoading);
    const listsError = useSelector(getCustomListsError);
    const detail = useSelector(getCustomListDetail);
    const isDetailLoading = useSelector(getCustomListDetailLoading);
    const detailError = useSelector(getCustomListDetailError);
    const categories = useSelector(getLocalCategoriesList);
    const selectedOrderId = location.state?.selectedOrderId as
        | string
        | undefined;

    const [itemSearch, setItemSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');

    useEffect(() => {
        if (propertyId) {
            dispatch(fetchCustomLists({ propertyId }));
        }
    }, [dispatch, propertyId]);

    useEffect(() => {
        if (listId) {
            dispatch(fetchCustomListDetail({ listId }));
        }
    }, [dispatch, listId]);

    useEffect(() => {
        setItemSearch('');
        setCategoryFilter('all');
    }, [listId]);

    const activeList = useMemo(
        () => lists.find((list) => list.id === listId) || null,
        [listId, lists],
    );

    const { openConfirmForList, confirmList, confirmMode, closeConfirm } =
        useAddListToOrderConfirm(listId, detail);

    const onSelectList = useCallback(
        (nextListId: string) => {
            navigate(getRouteOrdersAddItemsList(nextListId), {
                state: location.state,
            });
        },
        [location.state, navigate],
    );

    const panelItems = useMemo(() => {
        if (!detail || detail.id !== listId) {
            return [];
        }
        return detail.items || [];
    }, [detail, listId]);

    const filterOptions: FilterOption[] = useMemo(() => {
        if (!detail || detail.id !== listId) {
            return [];
        }

        const ids = new Set(
            (detail.items || [])
                .map(
                    (listItem) =>
                        getCustomListCatalogItem(listItem)?.categoryId,
                )
                .filter(Boolean),
        );

        return Object.values(categories || {})
            .filter((cat) => ids.has(cat.id))
            .map((cat) => ({ value: cat.id, label: cat.title }));
    }, [categories, detail, listId]);

    const canAddAll = Boolean(activeList && activeList.itemCount > 0);

    const isListLoading = Boolean(
        activeList && detail?.id !== activeList.id && !detailError,
    );

    const { allItemsOnOrder } = useListOrderCoverage(listId, detail);

    const onAddAllFromToolbar = useCallback(() => {
        if (!activeList) {
            return;
        }
        const activeItems =
            detail?.id === activeList.id ? detail.items : undefined;
        openConfirmForList(activeList, activeItems, 'add');
    }, [activeList, detail?.id, detail?.items, openConfirmForList]);

    const onRemoveAllFromToolbar = useCallback(() => {
        if (!activeList) {
            return;
        }
        const activeItems =
            detail?.id === activeList.id ? detail.items : undefined;
        openConfirmForList(activeList, activeItems, 'remove');
    }, [activeList, detail?.id, detail?.items, openConfirmForList]);

    const toolbarState = useMemo<ListsToolbarState | null>(() => {
        if (isListsLoading && lists.length === 0) {
            return null;
        }

        if (!isListsLoading && lists.length === 0) {
            return null;
        }

        return {
            itemSearch,
            setItemSearch,
            categoryFilter,
            setCategoryFilter,
            filterOptions,
            canAddAll,
            hasActiveList: Boolean(activeList),
            allItemsOnOrder,
            isListLoading,
            onAddAll: onAddAllFromToolbar,
            onRemoveAll: onRemoveAllFromToolbar,
        };
    }, [
        activeList,
        allItemsOnOrder,
        canAddAll,
        isListLoading,
        categoryFilter,
        filterOptions,
        isListsLoading,
        itemSearch,
        lists.length,
        onAddAllFromToolbar,
        onRemoveAllFromToolbar,
    ]);

    useRegisterListsToolbar(toolbarState);

    const onRetryLists = useCallback(() => {
        if (propertyId) {
            dispatch(fetchCustomLists({ propertyId }));
        }
    }, [dispatch, propertyId]);

    const onRetryDetail = useCallback(() => {
        if (listId) {
            dispatch(fetchCustomListDetail({ listId }));
        }
    }, [dispatch, listId]);

    const isPanelLoading = Boolean(
        activeList && isDetailLoading && detail?.id !== activeList.id,
    );

    const detailFailed = Boolean(
        activeList &&
            !isDetailLoading &&
            detail?.id !== activeList.id &&
            detailError,
    );

    return {
        lists,
        isListsLoading,
        listsError,
        listId,
        onSelectList,
        activeList,
        panelItems,
        itemSearch,
        categoryFilter,
        isPanelLoading,
        detailFailed,
        onRetryLists,
        onRetryDetail,
        confirmList,
        confirmMode,
        selectedOrderId,
        closeConfirm,
    };
};
